import React, {useEffect, useState} from "react";
import {Link, useHistory, useLocation, useParams} from "react-router-dom";
import {useSelector} from "react-redux";
import axios from "axios";
import {useTranslation} from "react-i18next";
import {FaPen} from "react-icons/fa";

import {attachClassToPsych} from "api/classes/attachClassToPsych";
import {useBackNavigation} from "components/BackButton";
import {ConfirmModal} from "components/ConfirmModal";
import {canEditSchool} from "helpers/schoolPermissions";
import {Layout} from "components/Layout";

/**
 * @api {get} /schools/:id/classes список классов
 * @apiGroup schools and classes
 * @apiName список классов
 *
 *  @apiParam {String} id ID школы
 *
 * @apiDescription <p>Страница со списком классов выбранной школы</p>
 * <p>Компонент <code>Classes.js</code> <a href="../client/src/pages/classes/Classes.js">ссылка на файл</a></p>
 */

/**
 * @api {get} /meetings/schools/:id/classes список классов при создании встречи
 * @apiGroup meetings
 * @apiName список классов при создании встречи
 *
 * @apiParam {String} id ID школы
 *
 * @apiDescription <p>Страница со списком классов при создании встречи</p>
 * <p>Компонент <code>Classes.js</code> <a href="../client/src/pages/classes/Classes.js">ссылка на файл</a></p>
 */

export const Classes = () => {
    const { t } = useTranslation("classes");
    const history = useHistory();
    const location = useLocation();
    const schoolId = useParams().id;
    const [classes, setClasses] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedClassId, setSelectedClassId] = useState(null);
    const currentUserId = useSelector((state) => state.auth.user?._id);
    const goBack = useBackNavigation();
    const isMeetingContext =
        location.pathname.indexOf("/meetings/schools/") !== -1 ||
        new URLSearchParams(location.search).get("context") === "meetings";

    // Определяем школу из первого класса (если есть)
    const school = classes[0]?.school;

    // Проверяем, может ли пользователь редактировать школу
    const canEdit = school && currentUserId ? canEditSchool(school, currentUserId) : false;

    useEffect(() => {
        if (schoolId) {
            fetchClasses();
        }
    }, [schoolId]);

    const fetchClasses = async () => {
        const { data } = await axios.get(`schools/${schoolId}/classes`);
        setClasses(data);
    };

    const handleAttachClass = (classId) => {
        setSelectedClassId(classId);
        setIsModalOpen(true);
    };

    const confirmAttachClass = async () => {
        try {
            await attachClassToPsych(selectedClassId);
            // Обновляем список классов после успешной привязки
            await fetchClasses();
        } catch (error) {
            console.error("Ошибка при привязке класса:", error);
        }
    };

    // Функция проверки, нужно ли показывать кнопку "Это мой класс"
    const shouldShowAttachButton = (classItem) => {
        // Показываем кнопку если:
        // 1. Класс не привязан ни к кому (attachedPsych === null/undefined)
        // 2. ИЛИ класс привязан к другому психологу (не к текущему пользователю)
        if (!classItem.attachedPsych) {
            return true; // Класс не привязан
        }
        return classItem.attachedPsych.id !== currentUserId; // Привязан к другому психологу
    };
    return (
        <Layout>
            <div className="page">
                <header className="page__header">
                    <button
                        onClick={() => {
                            if (isMeetingContext) {
                                if (history.length > 1) {
                                    goBack();
                                } else {
                                    history.replace("/meetings/schools?context=meetings");
                                }
                            } else {
                                history.push("/schools");
                            }
                        }}
                        className="icon-btn page__icon-btn page__icon-btn_left icon-btn_back"
                    />
                    <p className="page__title">{classes[0]?.school.name}</p>
                    {schoolId && canEdit && (
                        <Link to={`/schools/${schoolId}`} className="icon-btn page__icon-btn page__icon-btn_right">
                            <FaPen />
                        </Link>
                    )}
                </header>

                <div className="school-page__wrapper">
                    <div className="list">
                        {classes.map((item) => (
                            <div
                                key={item._id}
                                onClick={() => {
                                    // Проверяем контекст встреч через путь и query параметр
                                    const isMeetingContext =
                                        location.pathname.indexOf("/meetings/schools/") !== -1 ||
                                        new URLSearchParams(location.search).get("context") === "meetings";

                                    if (isMeetingContext) {
                                        history.push(`/meetings/classes/${item._id}/pupils?context=meetings`);
                                    } else {
                                        history.push(`/classes/${item._id}/pupils`);
                                    }
                                }}
                                className="list__item"
                            >
                                <p
                                    style={
                                        shouldShowAttachButton(item)
                                            ? { paddingRight: "10rem", overflowWrap: "break-word" }
                                            : undefined
                                    }
                                >
                                    {item.number} {item.letter}
                                </p>
                                <p className="list__desc">
                                    {t("pupils_number")}: {item.pupils.length}, {t("pupils_max")}: {item.max}
                                </p>
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        history.push(`/classes/${item._id}/add-pupils`);
                                    }}
                                    className="school-page__qr-icon"
                                />
                                {shouldShowAttachButton(item) && (
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleAttachClass(item._id);
                                        }}
                                        className="btn btn_secondary school-page__attach"
                                    >
                                        Это мой класс
                                    </button>
                                )}
                                <p className="list__psych-info">
                                    {item.attachedPsych ? (
                                        item.attachedPsych.id === currentUserId ? (
                                            // Текущий пользователь - выделяем зеленым и жирным
                                            <>
                                                <span style={{ fontWeight: 500 }}>Психолог:</span>{" "}
                                                <span style={{ color: "#28a745", fontWeight: 600 }}>
                                                    {item.attachedPsych.surname} {item.attachedPsych.name}
                                                    {item.attachedPsych.patronymic &&
                                                        ` ${item.attachedPsych.patronymic}`}
                                                </span>
                                            </>
                                        ) : (
                                            // Другой психолог - обычный стиль
                                            <>
                                                <span style={{ fontWeight: 500 }}>Психолог:</span>{" "}
                                                {item.attachedPsych.surname} {item.attachedPsych.name}
                                                {item.attachedPsych.patronymic && ` ${item.attachedPsych.patronymic}`}
                                            </>
                                        )
                                    ) : (
                                        <span style={{ color: "#999", fontStyle: "italic" }}>
                                            К классу не привязан психолог
                                        </span>
                                    )}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>

                {schoolId && (
                    <Link to={`/schools/${schoolId}/classes/create`} className="main-btn">
                        {t("add_class")}
                    </Link>
                )}
            </div>
            <ConfirmModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onConfirm={confirmAttachClass}
                title="Подтверждение действия"
                message="Это действие нельзя отменить. Вы уверены?"
                confirmText="Да"
                cancelText="Отмена"
            />
        </Layout>
    );
};
